<template>
  <div>
    <h4 class="member-details-form-section-header">Concession information</h4>
    <caption class="required-field-indicator">* Indicates required field</caption>
    <FormField id="concession-card-alert" v-if="showCardExpiredAlertMessage && hasConcessionCard === true">
      <Alert class="no-bottom-margin" :message="cardExpiredAlertMessage" alertType="info" />
    </FormField>
    <FormField id="concession-card-alert" v-if="showStateChangeAlertMessage && isEmptyString(hasConcessionCard)">
      <Alert class="no-bottom-margin" :message="stateChangeAlertMessage" alertType="info" />
    </FormField>
    <FormField id="concession-card-alert" v-if="!showStateChangeAlertMessage && ShowTurn65AlertMessage">
      <Alert class="no-bottom-margin" :message="ageTurn65AlertMessage" alertType="info" />
    </FormField>
    <FormField id="concession-card-alert" v-if="isReadOnly">
      <Alert class="no-bottom-margin" :message="readOnlyConcessionCardAlert" alertType="info" />
    </FormField>
    <div class="form-section">
      <div id="ref-data-concession-card" v-html="refData.concessionCardInformationText.informationText" />
    </div>
    <div class="form-section">
      <FormField
        labelText="Do you hold a concession card?"
        :helperText="
          showHasConcessionCardHelpText ? refData.concessionCardAdditionalFieldsHelpText.informationText : refData.concessionCardWithoutAdditionalFieldsHelpText.informationText
        "
      >
        <ul role="radiogroup" class="radio-group large">
          <li v-for="option in cardOptions" :key="`concession-card-declaration-${option.returnValue}`">
            <input
              type="radio"
              v-model="hasConcessionCard"
              :id="`concession-card-declaration-${option.returnValue}`"
              :value="option.returnValue"
              @click="switchDeclaration(option.returnValue)"
              :disabled="isReadOnly"
            />
            <label :for="`concession-card-declaration-${option.returnValue}`">{{ option.display }}</label>
          </li>
        </ul>
      </FormField>
    </div>
    <template v-if="concessionCardDeclarationStatus && hasConcessionCard">
      <div class="form-section" v-for="(concession, index) in memberDetails.concessionCard.cardList" :key="index">
        <FormField class="form-section" labelText="Which concession card do you hold?" :id="`${concession.cardType}-type`" :disabled="isReadOnly">
          <DropdownSelect
            :id="`${concession.cardType}-concession-card-${index}`"
            :name="`concessionCardName-${index}`"
            :items="getSelectOptionsLeft(index, memberDetails.concessionCard.cardList, concessionCardOptions)"
            v-model="concession.cardType"
            :initial-select="getDisplayValue(concession.cardType, concessionCardOptions) || 'Please select a concession card'"
            :value="concession.cardType"
            noDisabled
            @input="cardDetailUpdated(index, cardDetailUpdatedType.TYPE)"
            :disabled="isReadOnly"
          />
        </FormField>
        <FormField
          labelText="Card number"
          :id="`${concession.cardType}-number-${index}`"
          :placeholder="'XXXXXXXXXX'"
          type="text"
          class="form-section"
          v-model.lazy="concession.cardNumber"
          :error="getCardValidation(index)?.cardNumber?.$error"
          :errorMessage="getConcessionNumberErrorMessage(getCardValidation(index)?.cardNumber)"
          @input="cardDetailUpdated(index, cardDetailUpdatedType.NUMBER)"
          :disabled="isReadOnly"
        />
        <FormField
          labelText="Expiry date"
          :id="`${concession.cardType}-expiryDate-${index}`"
          type="text"
          class="form-section half-width"
          v-model.lazy.trim="concession.expiryDate"
          placeholder="DD / MM / YYYY"
          :error="getCardValidation(index)?.expiryDate?.$error || expiredCards.includes(Number(index)) || incorrectDateFormat.includes(Number(index))"
          v-maska="'## / ## / ####'"
          :errorMessage="getConcessionExpiryErrorMessage(getCardValidation(index)?.expiryDate, index)"
          :required="requiredExpiryConcessionCard(concession.cardType)"
          @input="cardDetailUpdated(index, cardDetailUpdatedType.EXPIRY_DATE)"
          :disabled="isReadOnly"
        />

        <div v-if="memberDetails.concessionCard.cardList.length > 1 && !isReadOnly">
          <button class="non-button-style" @click="removeSelectField(memberDetails.concessionCard.cardList, index)" type="button">Remove card</button>
        </div>
      </div>
      <div v-if="!isReadOnly" id="button-wrapper">
        <div class="form-section">
          <button
            class="non-button-style"
            type="button"
            v-if="memberDetails.concessionCard.cardList.length < concessionCardOptions.length"
            @click.prevent="addAnotherSelectField(memberDetails.concessionCard.cardList, concessionCardOptions)"
          >
            Add another concession card
          </button>
        </div>
        <div class="form-section" v-if="memberDetails.concessionCard.cardList.length > 1">You can store a maximum of 4 cards.</div>
      </div>
    </template>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import cloneDeep from 'lodash.clonedeep';
import { isAfter, addYears } from 'date-fns';
import MemberDetailsMixin from '@/views/MemberDetails/MemberDetailsFormSteps/MemberDetailsMixin';
import { showConcessionCardDetailsForm, ausStates, isEmptyString } from '@/utils';
import { ConcessionCardTypes, OperationType, UpdateMemberDetailsStepNames } from '@/enums';
import { ConcessionCardErrorMessage } from '@general/js/enums/ErrorMessageEnum';
import DropdownSelect from '@general/js/components/DropdownSelect.vue';
import { getCurrentDate, formatDate, isAgeOver65, getUTCTime, formatFormDate } from '@general/js/utils';
import useVuelidate from '@vuelidate/core';

export default {
  name: UpdateMemberDetailsStepNames.step7,

  setup() {
    return { v$: useVuelidate() };
  },

  mixins: [MemberDetailsMixin],

  components: {
    DropdownSelect,
  },

  props: {
    initialData: {
      type: Object,
    },

    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      cardOptions: [
        { display: 'Yes', returnValue: true },
        { display: 'No', returnValue: false },
      ],
      hasConcessionCard: null,
      showHasConcessionCardHelpText: false,
      cardDetailUpdatedType: {
        TYPE: 'cardType',
        NUMBER: 'cardNumber',
        EXPIRY_DATE: 'expiryDate',
      },
      categoryType: {
        CATEGORY1: 'CATEGORY1',
        CATEGORY2: 'CATEGORY2',
        CATEGORY3: 'CATEGORY3',
      },
      expiredCards: [],
      incorrectDateFormat: [],
      stateChangeAlertMessage: '',
      showStateChangeAlertMessage: false,
      ageTurn65AlertMessage: '',
      cardExpiredAlertMessage: '',
      readOnlyConcessionCardAlert:
        'If you would like to make changes to your details below, please <a href="tel:1800335425">contact us on 1800 335 425</a>. Our friendly team will be happy to help you.',
    };
  },

  // validations() {
  //   if (this.concessionCardDeclarationStatus && this.hasConcessionCard) {
  //     return {
  //       memberDetails: {
  //         concessionCard: {
  //           cardList: {
  //             required,
  //             // For each concession card:
  //             $each: {
  //               required,
  //               cardType: {
  //                 required: requiredIf(function () {
  //                   return !this.isReadOnly;
  //                 }),
  //               },
  //               cardNumber: {
  //                 required: requiredIf(function () {
  //                   return !this.isReadOnly;
  //                 }),
  //                 isValidConcessionCardNumber: (value, option) => {
  //                   const cardType = option.cardType.toLowerCase();
  //                   if (
  //                     cardType === ConcessionCardTypes.PCC.toLowerCase() ||
  //                     cardType === ConcessionCardTypes.HCC.toLowerCase() ||
  //                     cardType === ConcessionCardTypes.CSHC.toLowerCase()
  //                   ) {
  //                     return invalidConcessionCardNumber(value);
  //                   }
  //                   return true;
  //                 },
  //               },
  //               expiryDate: {
  //                 required: requiredIf(function (concessionCard) {
  //                   return this.requiredExpiryConcessionCard(concessionCard.cardType) && !this.isReadOnly;
  //                 }),
  //                 isLaterDate: (value, option) => {
  //                   const cardType = option.cardType.toLowerCase();
  //                   if (value !== '' && this.v$.$anyDirty && this.requiredExpiryConcessionCard(cardType)) {
  //                     return !isPastDateIncludingToday(value);
  //                   }
  //                   return true;
  //                 },
  //                 // validate the date
  //                 IS_INVALID: (value) => {
  //                   return DATE_VALIDATOR(value);
  //                 },
  //               },
  //             },
  //           },
  //           hasConcessionCard: {
  //             required: requiredIf(function () {
  //               return !this.isReadOnly;
  //             }),
  //           },
  //         },
  //       },
  //     };
  //   }

  //   return {
  //     memberDetails: {
  //       concessionCard: {
  //         hasConcessionCard: {
  //           required: requiredIf(function () {
  //             return !this.isReadOnly;
  //           }),
  //         },
  //       },
  //     },
  //   };
  // },

  beforeMount() {
    this.showHasConcessionCardHelpText = showConcessionCardDetailsForm(
      this.memberDetails?.memberInformation?.residentialAddress?.state,
      this.memberDetails.memberInformation.dateOfBirth,
      this.memberDetails.membershipSummary.extrasOnly,
      this.memberDetails.concessionCard.hasConcessionCard,
    );

    this.hasConcessionCard = this.memberDetails.concessionCard?.hasConcessionCard;
    this.updateMemberDetailsFormValid(true);
    // if (!this.hasConcessionCard) {
    //   this.updateMemberDetailsFormValid(false);
    // } else {
    //   this.updateMemberDetailsFormValid(this.v$.$invalid);
    // }

    this.ageTurn65AlertMessage = this.refData.concessionCardTurning65AlertText.informationText;
    this.cardExpiredAlertMessage = this.refData.concessionCardExpiryWarningText.informationText;
  },

  mounted() {
    this.$watch('memberDetails.concessionCard.hasConcessionCard', function (val) {
      // Updated lastModified time when declaration change
      if (!isEmptyString(val)) {
        this.memberDetails.concessionCard.lastModified = getUTCTime();
      }
    });
  },

  watch: {
    hasConcessionCard(newBool) {
      this.memberDetails.concessionCard.hasConcessionCard = newBool;

      // if changing from `true` to `false` OR the members circumstances don't require specific concession details
      /**
       * NOTE: When changing from `Yes` to `No`, expected to add new card obj with card type to be `NONE` and any existing
       * current active cards to have today's date as end date. unlike all the other operation, for this scenario, it is
       * handled by EAI as this logic was already built from EAI end prior from FE work.
       *
       * In this scenario, what EAI is expecting `hasConcessionCard` to be `false` and `cardList` array to be empty array.
       */
      if (newBool === false || !this.concessionCardDeclarationStatus) {
        this.memberDetails.concessionCard.cardList = [];
      }
    },

    'v$.memberDetails.$model.memberInformation.residentialAddress': {
      handler(newAddress, oldAddress) {
        this.addressChangeHandler(newAddress, oldAddress);
      },
      deep: true,
    },
  },

  computed: {
    concessionCardOptions() {
      if (this.refData) {
        return this.refData.concessionCardOptions ?? [];
      }
      return [];
    },
    // Show expired card message
    showCardExpiredAlertMessage() {
      const extrasOnlyProduct = this.memberDetails?.membershipSummary?.extrasOnly;
      const state = this.memberDetails?.memberInformation?.residentialAddress.state;
      // only apply when not an extras only product and from nsw/act
      if (!extrasOnlyProduct && (state?.toUpperCase() === 'NSW' || state?.toUpperCase() === 'ACT') && this.refData?.concessionCardExpiryWarningText?.informationText) {
        // cardlist will need to be checked since it will be empty if hasConcessionCard is false
        if (this.memberDetails.concessionCard && this.memberDetails.concessionCard?.cardList) {
          const cardList = this.memberDetails.concessionCard.cardList;
          // if any card is expired
          this.updateExpiredCardIncorrectDateFormatLists(cardList);
          if (this.expiredCards.length > 0) {
            return true;
          }
        }
      }
      return false;
    },

    // Show turning 65 alert message, it only happens when NSW/ACT+ExtraOnly and VIC/WA/NT
    ShowTurn65AlertMessage() {
      const extrasOnlyProduct = this.memberDetails?.membershipSummary?.extrasOnly;
      const state = this.memberDetails?.memberInformation?.residentialAddress.state;

      // only display after user turns 65 and hasn't updated concession card info
      if (([ausStates.NSW, ausStates.ACT].includes(state?.toLowerCase()) && extrasOnlyProduct) || [ausStates.VIC, ausStates.WA, ausStates.NT].includes(state?.toLowerCase())) {
        if (isAgeOver65(this.memberDetails.memberInformation.dateOfBirth)) {
          if (isEmptyString(this.memberDetails.concessionCard.lastModified)) {
            return true;
          }
          if (isAfter(addYears(new Date(this.memberDetails.memberInformation.dateOfBirth), 65), new Date(this.memberDetails.concessionCard.lastModified))) {
            return true;
          }
        }
      }
      return false;
    },
  },

  methods: {
    // This shouldn't run --> as not details will be getting updated as they are all disabled
    cardDetailUpdated(cardIndex, updateType) {
      const thisCardValidationObj = this.memberDetails.concessionCard.cardList[cardIndex];
      const concessionCardObj = this.memberDetails.concessionCard;
      const initialData = this.initialData.concessionCard;
      const targetCardObj = concessionCardObj.cardList.at(cardIndex);
      const initialCardList = concessionCardObj.initialCardList;
      let cardIndexInitialList;

      initialCardList?.forEach((card, index) => {
        const sameIds = card.id === targetCardObj.id;
        const sameTypeAndNumber = card.cardType === targetCardObj.cardType && card.cardNumber === targetCardObj.cardNumber;
        if (sameIds || sameTypeAndNumber) {
          cardIndexInitialList = index;
        }
      });
      if (cardIndexInitialList !== undefined) {
        const targetCardInitial = initialCardList[cardIndexInitialList];
        const targetCardToCompare = cloneDeep(targetCardObj);
        const targetCardInitialToCompare = cloneDeep(targetCardInitial);
        const todayDate = this.getCurrentDate();

        switch (updateType) {
          case this.cardDetailUpdatedType.TYPE:
            if (!isEqual(targetCardObj.cardType, targetCardInitial.cardType)) {
              targetCardInitial.op = this.OperationType.REMOVE;
              targetCardObj.op = this.OperationType.ADD;
            } else {
              targetCardInitial.op = this.OperationType.NONE;
              targetCardObj.op = this.OperationType.NONE;

              if (!isEqual(targetCardObj.expiryDate, targetCardInitial.expiryDate)) {
                targetCardInitial.op = this.OperationType.NONE;
                targetCardObj.op = this.OperationType.REPLACE;
              }

              if (!isEqual(targetCardObj.cardNumber, targetCardInitial.cardNumber)) {
                targetCardInitial.op = this.OperationType.REMOVE;
                targetCardObj.op = this.OperationType.ADD;
              }
            }
            break;

          case this.cardDetailUpdatedType.NUMBER:
            // Only excute following logic when card number input is valid
            if (!thisCardValidationObj.cardNumber) {
              if (!isEqual(targetCardObj.expiryDate, targetCardInitial.expiryDate) && isEqual(targetCardObj.cardNumber, targetCardInitial.cardNumber)) {
                targetCardInitial.op = this.OperationType.NONE;
                targetCardObj.op = this.OperationType.REPLACE;
              } else if (
                ['expiryDate', 'cardNumber', 'cardType'].every((key) => {
                  return targetCardObj[key] === targetCardInitial[key];
                })
              ) {
                // if newly input card number is same as initial card number and both expiry date and card type are same
                targetCardInitial.op = this.OperationType.NONE;
                targetCardObj.op = this.OperationType.NONE;
              } else {
                targetCardInitial.op = this.OperationType.REMOVE;
                targetCardObj.op = this.OperationType.ADD;
              }
            }
            break;

          case this.cardDetailUpdatedType.EXPIRY_DATE:
            // only update `op` to replace when it is not newly added
            if (targetCardObj.op !== this.OperationType.ADD && !isEqual(targetCardObj.expiryDate, targetCardInitial.expiryDate)) {
              targetCardObj.op = this.OperationType.REPLACE;
            }
            break;
          default:
            break;
        }

        switch (targetCardInitial.op) {
          case this.OperationType.REMOVE:
            targetCardInitial.endDate = todayDate;
            break;

          default:
            if (initialData.initialCardList && 'endDate' in initialData.initialCardList[cardIndexInitialList]) {
              targetCardInitial.endDate = initialData.initialCardList[cardIndexInitialList].endDate;
            } else {
              delete targetCardInitial.endDate;
            }
            break;
        }

        switch (targetCardObj.op) {
          case this.OperationType.ADD:
            targetCardObj.startDate = todayDate;
            break;

          default:
            targetCardObj.startDate = initialData.cardList[cardIndex].startDate;
            break;
        }

        delete targetCardToCompare.op;
        delete targetCardInitialToCompare.op;

        if (isEqual(targetCardToCompare, targetCardInitialToCompare)) {
          targetCardInitial.op = this.OperationType.NONE;
          targetCardObj.op = this.OperationType.NONE;
        }

        if (targetCardInitial.op === this.OperationType.REMOVE) {
          targetCardObj.id = null;
        } else {
          targetCardObj.id = targetCardInitial.id;
        }
      }
    },

    // To add another object for selected concession card
    addAnotherSelectField(selectedOptions) {
      const firstSelection = JSON.parse(JSON.stringify(selectedOptions[0]));
      Object.keys(firstSelection).forEach((key) => {
        firstSelection[key] = '';
      });
      firstSelection.op = this.OperationType.ADD;
      firstSelection.startDate = this.getCurrentDate();
      delete firstSelection.id;
      delete firstSelection.endDate;

      selectedOptions.push(firstSelection);
    },

    // get the actual display value from the relative api data list
    getDisplayValue(modelValue, listName) {
      return listName.find((el) => {
        return el.returnValue.toLowerCase() === modelValue.toLowerCase();
      })?.display;
    },

    // To obtain a new list of select options after adding concession card
    getSelectOptionsLeft(index, selectedOptions, allOptions) {
      const options = [];

      for (let i = 0; i < selectedOptions.length; i++) {
        if (i !== parseInt(index, 10)) {
          options.push(selectedOptions[i].cardType);
        }
      }

      // transform data to have name/value keys for dropDown
      const transformedOptions = allOptions.map((card) => {
        return {
          name: card.display,
          value: card.returnValue,
        };
      });

      return transformedOptions.filter((el) => {
        return !options.includes(el.value);
      });
    },

    // To remove an object from concession card
    removeSelectField(selectedOptions, index) {
      let selectedData = selectedOptions;
      if (selectedData.length > 1) {
        selectedData = selectedData.splice(index || selectedOptions.length - 1, 1);
      }

      const updatedCardList = [];
      this.memberDetails?.concessionCard?.initialCardList?.map((card) => {
        if (selectedData[0].cardType === card.cardType) {
          updatedCardList.push({
            ...card,
            endDate: this.getCurrentDate(),
            op: OperationType.REMOVE,
          });
        } else {
          updatedCardList.push({
            ...card,
          });
        }

        return true;
      });

      const newMemberDetails = { ...this.memberDetails };
      newMemberDetails.concessionCard.initialCardList = updatedCardList;
      this.updateMemberDetails(newMemberDetails);
    },

    // switch between YES/NO option
    // if NO set "concessionDeclaration" false otherwise prefilled the fields
    switchDeclaration(hasConcessionCard) {
      this.hasConcessionCard = hasConcessionCard;
      if (!this.hasConcessionCard) {
        this.updateMemberDetailsFormValid(false);
        this.updateConcessionCard(false);
      } else {
        this.updateConcessionCard(true);
        if (this.showHasConcessionCardHelpText) {
          // this.updateMemberDetailsFormValid(this.v$.$invalid);
          this.updateMemberDetailsFormValid(true);
        }
        this.updateMemberDetailsFormValid(false);
      }
    },

    updateConcessionCard(hasConcessionCard) {
      const newMemberDetails = { ...this.memberDetails };
      const concessionCard = cloneDeep(this.initialData?.concessionCard);
      newMemberDetails.concessionCard = {
        ...concessionCard,
        hasConcessionCard,
      };
      this.updateMemberDetails(newMemberDetails);
    },

    updateExpiredCardIncorrectDateFormatLists(cardList) {
      const expiredList = [];
      const incorrectDateFormat = [];
      cardList.forEach(function (obj, index) {
        const regex = new RegExp(/^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])$/); // Regex to check correct date format in form DD/MM/YYYY and is a valid date (32/12/2022 will be considered invalid)
        const expiryDate = formatFormDate(obj.expiryDate);
        // check correct date format
        if (regex.test(expiryDate)) {
          // check for date is past or current date
          if (new Date(expiryDate) <= new Date(getCurrentDate())) {
            expiredList.push(index);
          }
        } else if (expiryDate !== '') {
          incorrectDateFormat.push(index);
        }
      });
      this.expiredCards = expiredList;
      this.incorrectDateFormat = incorrectDateFormat;
    },

    requiredExpiryConcessionCard(cardType) {
      const concessionCardTypeLower = cardType.toLowerCase();
      if (
        concessionCardTypeLower === ConcessionCardTypes.HCC.toLowerCase() ||
        concessionCardTypeLower === ConcessionCardTypes.PCC.toLowerCase() ||
        concessionCardTypeLower === ConcessionCardTypes.CSHC.toLowerCase() // TODO: Add these checks to concessionUtils file
      ) {
        return true;
      }
      return false;
    },

    getConcessionNumberErrorMessage(input) {
      if (input?.$error && !input.required) return ConcessionCardErrorMessage.IS_NUMBER_REQUIRED;
      if (input?.$error && !input.isValidConcessionCardNumber) return ConcessionCardErrorMessage.IS_INVALID_NUMBER;
      return '';
    },

    getConcessionExpiryErrorMessage(input, index) {
      if (input?.$error && !input.IS_INVALID) return ConcessionCardErrorMessage.IS_INVALID_EXPIRY_DATE;
      if (input?.$error && !input.isPastDateIncludingToday) return ConcessionCardErrorMessage.IS_PAST_DATE;

      if ((!input?.isPastDateIncludingToday && this.expiredCards.includes(Number(index))) || this.incorrectDateFormat.includes(Number(index)))
        return ConcessionCardErrorMessage.IS_PAST_DATE;
      return '';
    },

    // If residential state change, then show this alert message
    updateStateChange(newState, oldState) {
      const isExtrasOnlyProduct = this.memberDetails.membershipSummary.extrasOnly;
      const DOB = this.memberDetails.memberInformation.dateOfBirth;
      this.stateChangeAlertMessage = '';

      const newStateCategory = this.getCategoryLabel(newState, DOB, isExtrasOnlyProduct);
      const oldStateCategory = this.getCategoryLabel(oldState, DOB, isExtrasOnlyProduct);

      if (oldStateCategory === this.categoryType.CATEGORY1) {
        switch (newStateCategory) {
          case this.categoryType.CATEGORY1:
            // 1>1
            this.stateChangeAlertMessage = this.refData.concessionCardAdditionalInformationAlertText.informationText;
            break;
          case this.categoryType.CATEGORY2:
            // 1>2
            this.stateChangeAlertMessage = this.refData.concessionCardAdditionalInformationAlertText.informationText;
            break;
          case this.categoryType.CATEGORY3:
            // 1>3
            break;
          default:
            break;
        }
      }

      if (oldStateCategory === this.categoryType.CATEGORY2) {
        switch (newStateCategory) {
          case this.categoryType.CATEGORY1:
            // 2>1
            this.stateChangeAlertMessage = this.refData.concessionCardAdditionalInformationAltAlertText.informationText;
            break;
          case this.categoryType.CATEGORY2:
            // 2>2
            this.stateChangeAlertMessage = this.refData.concessionCardAdditionalInformationAlertText.informationText;
            break;
          case this.categoryType.CATEGORY3:
            // 2>3
            break;
          default:
            break;
        }
      }

      if (oldStateCategory === this.categoryType.CATEGORY3) {
        switch (newStateCategory) {
          case this.categoryType.CATEGORY1:
            // 3>1
            this.stateChangeAlertMessage = this.refData.concessionCardDeclarationAlertText.informationText;
            break;
          case this.categoryType.CATEGORY2:
            // 3>2
            this.stateChangeAlertMessage = this.refData.concessionCardAdditionalInformationAlertText.informationText;
            break;
          case this.categoryType.CATEGORY3:
            // 3>3
            break;
          default:
            break;
        }
      }

      /**
       * Any previous active card information (if exists) will no longer be relevant
       * an end date of when they moved state will be applied to previous active card record.
       */
      const stateModifiedTime = this.memberDetails.memberInformation.residentialAddress.stateLastModified;
      if (this.initialData.concessionCard.hasConcessionCard && !isEmptyString(stateModifiedTime)) {
        const newCardList = [...this.memberDetails.concessionCard?.cardList];
        const stateChangeDate = formatDate(new Date(stateModifiedTime));
        newCardList.forEach((card) => {
          if ('endDate' in card) {
            // The cards that have an end date in the past or today will not be shown on the screen, as these cards are considered “removed”.
            if (isAfter(new Date(card.endDate), new Date(stateChangeDate))) {
              card.endDate = stateChangeDate;
            }
          } else {
            card.endDate = stateChangeDate;
          }
        });
      }

      // Reset concession declaration to be unseleted
      this.hasConcessionCard = null;
    },

    // For compare two address
    standardizeAddress(addressObj) {
      const newAddress = { ...addressObj };
      Object.keys(newAddress).forEach((key) => {
        if (isEmptyString(newAddress[key]) || key === 'stateLastModified') {
          newAddress[key] = '';
        }
      });

      return newAddress;
    },

    // Mapping member details to category
    getCategoryLabel(state, dob, extrasOnly) {
      let categoryLabel = '';

      switch (state.toLowerCase()) {
        case ausStates.NSW:
        case ausStates.ACT:
          if (!extrasOnly) categoryLabel = this.categoryType.CATEGORY1;
          else categoryLabel = isAgeOver65(dob) ? this.categoryType.CATEGORY2 : this.categoryType.CATEGORY3;
          break;
        case ausStates.VIC:
        case ausStates.WA:
        case ausStates.NT:
          categoryLabel = isAgeOver65(dob) ? this.categoryType.CATEGORY2 : this.categoryType.CATEGORY3;
          break;
        case ausStates.QLD:
        case ausStates.TAS:
        case ausStates.SA:
          categoryLabel = this.categoryType.CATEGORY3;
          break;
        default:
          break;
      }

      return categoryLabel;
    },

    getCardValidation(index) {
      return this.v$?.memberDetails?.concessionCard?.cardList?.$each?.$response?.$data?.[index];
    },

    addressChangeHandler(newAddress, oldAddress) {
      if (!isEqual(newAddress, oldAddress)) {
        const initialResidentialAddress = this.standardizeAddress(this.initialData.memberInformation.residentialAddress);
        const currentResidentialAddress = this.standardizeAddress(newAddress);
        const isInitialAddress = isEqual(initialResidentialAddress, currentResidentialAddress);

        if (!isInitialAddress) {
          if (newAddress.state !== initialResidentialAddress.state) {
            this.showStateChangeAlertMessage = true;
            this.updateStateChange(newAddress.state, initialResidentialAddress.state);
          } else {
            this.showStateChangeAlertMessage = false;
          }
        } else {
          // If it's same with initial address, the concession card information will also revert back to initial details.
          this.updateConcessionCard(this.initialData.concessionCard.hasConcessionCard);
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.form-section {
  margin-bottom: 25px;

  #ref-data-concession-card {
    $COLUMN_GAP: 40px;

    @include desktop {
      column-gap: $desktop_spacing;
    }

    @include tablet {
      column-gap: $tablet_spacing;
    }

    @include mobile {
      column-gap: $mobile_spacing;
      grid-template-columns: 100%;
      grid-template-rows: auto;
    }

    display: grid;
    grid-template-columns: 2fr 1fr;
    grid-template-rows: auto;

    :deep(p) {
      margin-bottom: 0;
    }
  }

  .form-field {
    margin-bottom: 25px;

    @include tablet-and-above {
      :deep(.item3 p) {
        margin-bottom: 0;
        p {
          margin-bottom: 0;
        }
      }
    }
  }

  @media screen and (min-width: 767px) {
    &:last-child {
      grid-template-columns: 1fr 2fr;
    }
  }
}

#button-wrapper {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  margin-bottom: 5px;
}
</style>
